<template>

  <div :class="desktopViewClass"
    v-bind:style="isDesktop ? { backgroundImage: 'url(' + backgroundTileUrl + ')', backgroundColor: backgroundColor, backgroundSize: backgroundSize, backgroundRepeat: backgroundRepeat } : {}">
    <!-- Demo notification outside container for full width on desktop -->
    <div v-if="isDemo && isDesktop && !loading" class="desktop-demo-header" role="alert">
      <div class="alert alert-danger text-center">
        <h1 class="h4 mb-2 sr-only">{{ $t('Accessibility.DemoMode') }}</h1>
        This is a <strong>demo</strong> page {{ demoInfo }}
      </div>
    </div>

    <location-blur :buttonText="locationButtonText" :enabled="locationCheckNeeded" :introText="locationIntroText"
      :multipleLocationsFound="foundLocations.length > 0" :multipleLocationsFoundText="multipleLocationsFoundText"
      @location="onLocationReceived" buttonClass="btn btn-block btn-success btn-small" class="bg"
      errorTextClass="text-center text-danger" introTextClass="text-center" ref="locationControl"
      v-bind:style="!isDesktop ? { backgroundImage: 'url(' + backgroundTileUrl + ')', backgroundColor: backgroundColor, backgroundSize: backgroundSize, backgroundRepeat: backgroundRepeat } : {}">
      <template slot="locations">
        <label for="location-select" class="sr-only">{{ $t('Accessibility.SelectLocation') }}</label>
        <select id="location-select" @change="onLocationSelected($event)" class="form-control custom-select"
          :aria-label="$t('Accessibility.SelectLocation')">

          <option :value="null">{{ $t('Accessibility.ChooseLocation') }}</option>

          <option v-bind:value="found.value" v-for="found in foundLocations" :key="found.value">
            {{ found.text }}
          </option>

        </select>
      </template>

      <div role="main" aria-live="polite">

        <!-- Loading indicator with proper accessibility -->
        <div class="spinner spinner-background" v-if="loading" role="status" aria-live="polite"
          :aria-label="$t('Accessibility.LoadingGameContent')">
          <span class="sr-only">{{ $t('Accessibility.Loading') }}</span>
        </div>

        <!-- Demo notifications with proper semantics (hidden on desktop, shown at top instead) -->
        <div v-if="isDemo && !isDesktop" role="alert">
          <div v-if="runningInIFrame" class="alert-inframe alert-danger text-center">
            <h1 class="h4 mb-2 sr-only">{{ $t('Accessibility.DemoMode') }}</h1>
            This is a <strong>demo</strong> page {{ demoInfo }}
          </div>
          <div v-else class="alert alert-danger text-center">
            <h1 class="h4 mb-2 sr-only">{{ $t('Accessibility.DemoMode') }}</h1>
            This is a <strong>demo</strong> page {{ demoInfo }}
          </div>
        </div>


        <!-- Game Timer (Top Position) -->
        <section v-show="!loading && !timerEnds && showTimer && timerPosition === 'Top'"
          :style="isDesktop ? (isDemo ? 'padding-top: 60px;' : 'padding-top: 40px;') : 'padding-top: 60px;'" role="region"
          aria-labelledby="timer-section-title-top">
          <h2 id="timer-section-title-top" class="sr-only">Game Timer</h2>
          <component :is="countdownComponent" :stop="endOfGame" :font-family="fontFamily"
            :tenths-mode="timerTenthsMode" :urgent-color="timerUrgentColor" @end="timerExpired" @stopped="timerStopped"
            ref="visualCountdownTop" role="timer" aria-live="polite" aria-label="Game countdown timer">
          </component>
        </section>

        <!-- Scratch and Win Game -->
        <section v-if="!loading && type === 'ScratchAndWin' && !timerEnds"
          :style="isDesktop ? (isDemo ? 'padding-top: 80px;' : 'padding-top: 60px;') : 'padding-top: 60px;'"
          role="region" aria-labelledby="scratch-game-title">
          <h2 id="scratch-game-title" class="sr-only">Scratch and Win Game</h2>
          <scratch-and-win :data="scratchAndWin" :isDemo="isDemo" :font-family="fontFamily" :font-size="fontSize"
            :viewport-dimensions="getViewportDimensions()" @end="onGameDone" @percentage="onScratchPercentage"
            @start="onGameStart">
          </scratch-and-win>
        </section>

        <!-- Wheel of Fortune Game -->
        <section :style="isDesktop ? (isDemo ? 'padding-top: 80px;' : 'padding-top: 60px;') : 'padding-top: 60px;'"
          v-if="!loading && type === 'WheelOfFortune' && !timerEnds" role="region" aria-labelledby="wheel-game-title">
          <h2 id="wheel-game-title" class="sr-only">Wheel of Fortune Game</h2>
          <wheel-of-fortune :data="wheelOfFortune" :isDemo="isDemo" :inFrame="runningInIFrame"
            :viewport-dimensions="getViewportDimensions()" @end="onGameDone" @start="onGameStart">
          </wheel-of-fortune>
        </section>

        <!-- Tetris Game -->
        <section v-if="!loading && type === 'Tetris' && !timerEnds" role="region" aria-labelledby="tetris-game-title">
          <h2 id="tetris-game-title" class="sr-only">Tetris Game</h2>
          <tetris :id="id" :languageId="languageId"></tetris>
        </section>

        <!-- Memory Game -->
        <section v-if="!loading && type === 'MemoryGame' && !timerEnds" style="padding-top: 15px" role="region"
          aria-labelledby="memory-game-title">
          <h2 id="memory-game-title" class="sr-only">Memory Game</h2>
          <memory-game :data="memoryGame" :font-family="fontFamily" :font-size="fontSize" :isDemo="isDemo"
            :inFrame="runningInIFrame" :viewport-dimensions="getViewportDimensions()" @end="onGameDone"
            @start="onGameStart">
          </memory-game>
        </section>

        <!-- Slot Machine Game -->
        <section v-if="!loading && type === 'SlotMachine' && !timerEnds"
          :style="isDesktop ? 'padding-top: 15px' : 'padding-top: 15px; overflow-x: hidden; max-width: 100vw;'"
          role="region" aria-labelledby="slot-game-title">
          <h2 id="slot-game-title" class="sr-only">Slot Machine Game</h2>
          <slot-machine :data="slotMachine" :font-family="fontFamily" :font-size="fontSize" :isDemo="isDemo"
            :inFrame="runningInIFrame" :viewport-dimensions="getViewportDimensions()" @end="onGameDone"
            @start="onGameStart">
          </slot-machine>
        </section>


        <!-- Game Timer (Bottom Position) -->
        <section v-show="!loading && !timerEnds && showTimer && timerPosition !== 'Top'" style="padding-top: 20px" role="region"
          aria-labelledby="timer-section-title">
          <h2 id="timer-section-title" class="sr-only">Game Timer</h2>
          <component :is="countdownComponent" :stop="endOfGame" :font-family="fontFamily"
            :tenths-mode="timerTenthsMode" :urgent-color="timerUrgentColor" @end="timerExpired" @stopped="timerStopped"
            ref="visualCountdown" role="timer" aria-live="polite" aria-label="Game countdown timer">
          </component>
        </section>

        <!-- Game Results and Actions -->
        <section v-if="endOfGame" role="region" aria-labelledby="results-section-title">
          <h2 id="results-section-title" class="sr-only">Game Results</h2>

          <div class="text-center m-3" v-for="action in actions" :key="action.identifier"
            :ref="'action' + action.identifier" v-if="!afterButtonPressedText">

            <!-- Action description text -->
            <div :style="actionTextStyle(action)" v-if="action.text" role="status" aria-live="polite">
              <span v-html="action.text" />
            </div>

            <!-- Action button -->
            <button :style="buttonStyle(action)" @click="onButtonClicked(action.identifier)"
              @mouseenter="setActionHoverState(action, true)" @mouseleave="setActionHoverState(action, false)"
              @keydown.enter="onButtonClicked(action.identifier)"
              @keydown.space.prevent="onButtonClicked(action.identifier)" class="btn btn-block"
              v-if="action.buttonText && !action.noButtonClickRequiredForAutoSend"
              :aria-label="getButtonAriaLabel(action)"
              :aria-describedby="action.text ? 'action-desc-' + action.identifier : null" v-text="action.buttonText">
            </button>

            <!-- Hidden link for URL actions -->
            <a :href="action.actionValue" :ref="'action' + action.identifier" :target="runningInIFrame ? '_blank' : ''"
              :aria-label="'Open ' + action.actionValue + (runningInIFrame ? ' in new tab' : '')" style="display: none"
              v-if="action.type === 'Url'">
            </a>

            <div style="height: 15px" aria-hidden="true"></div>

          </div>

          <!-- Post-action feedback -->
          <div class="text-center m-3 pb-3" v-if="afterButtonPressedText" role="status" aria-live="polite">
            <div :style="afterButtonPressedTextStyle()" class="h4">
              {{ afterButtonPressedText }}
            </div>
          </div>

        </section>

      </div>


    </location-blur>
  </div>

</template>

<script>
import axios from 'axios';
import LightenDarkenColor from "./../utils/color";
import { pack, unpack } from './../utils/pack';
import MobileDetect from "mobile-detect";


import LocationBlur from '../components/controls/LocationBlur';
import VisualCountdown from '../components/countdown';
import ModernCountdown from '../components/countdown-modern';


export default {
  components: {
    VisualCountdown,
    ModernCountdown,
    LocationBlur,
    ScratchAndWin: () => import('../components/ScratchAndWin'),
    WheelOfFortune: () => import('../components/WheelOfFortune'),
    Tetris: () => import('../components/Tetris'),
    MemoryGame: () => import('../components/MemoryGame'),
    SlotMachine: () => import('../components/SlotMachine')
  },
  provide() {
    return {
      getViewportDimensions: this.getViewportDimensions,
      isDesktopView: () => this.isDesktop
    }
  },
  data: function () {
    return {
      loading: true,
      id: '',

      underMaintenance: false,

      languageId: 'EN',
      isDemo: false,
      success: true,
      alreadyRedeemed: false,
      codeInvalid: false,
      expired: false,

      type: '',
      backgroundTileUrl: '',
      backgroundSize: 'auto',
      backgroundRepeat: 'repeat',
      backgroundColor: 'white',
      timerDuration: 0,
      timerLeftOver: 0,
      timerTextColor: '',
      timerBackgroundColor: '',
      timerTopBackgroundColor: '',
      timerTopTextColor: '',
      timerPosition: 'Bottom',
      timerStyle: 'Default',
      timerTenthsMode: 'seconds',
      timerUrgentColor: '#ff4444',
      showTimer: false,
      timerEnds: false,
      endOfGame: false,
      fontFamily: null,
      fontSize: null,

      scratchAndWin: undefined,
      wheelOfFortune: undefined,
      tetris: undefined,
      memoryGame: undefined,
      slotMachine: undefined,

      actions: [],
      afterButtonPressedText: '',
      afterButtonPressedTextColor: '',
      afterButtonPressedTextBackgroundColor: '',
      textBackgroundColor: '',

      isDesktop: false,

      runningInIFrame: false,
      demoInfo: '',


      actionHoverState: false,
      actionHoverIdentifier: '',

      locationCheckNeeded: false,
      locationButtonText: '',
      locationIntroText: '',
      invalidLocationText: '',
      multipleLocationsFoundText: '',
      foundLocations: [],
      geolocationVenueId: '',
      isDemoWithValidLocation: false,
      isDemoWithInvalidLocation: false,
      isDemoWithMultipleLocations: false,
    }
  },

  created() {

    if (this.underMaintenance) {
      this.$router.replace('/notfound?maintenance=true')
      return;
    }

    // Improved iframe detection for mobile devices
    this.runningInIFrame = this.detectIframe();
    if (this.runningInIFrame) {

    }

    this.id = this.$route.params.id;

    // Extract language from URL query parameter first, then fallback to ID parameter
    let language = this.$route.query.language;
    if (!language && this.id) {
      // Extract language from ID if it follows the pattern ending with _NL, _EN, etc.
      const idParts = this.id.split('_');
      if (idParts.length >= 2) {
        const potentialLang = idParts[idParts.length - 1];
        if (potentialLang.length === 2 && /^[A-Z]{2}$/.test(potentialLang)) {
          language = potentialLang;
        }
      }
    }

    // Set locale immediately before API call to prevent fallback warnings
    if (language) {
      this.$i18n.locale = language.toLowerCase();
      this.languageId = language.toUpperCase();
    }

    this.loading = true;
    let vm = this;

    let query = '';
    if (language)
      query = '?language=' + language;


    const data = { code: vm.id };
    const packedData = pack(data);

    axios.post(process.env.VUE_APP_API_URL + 'play-data' + query, { _data: packedData._data })
      .then(response => {
        const packedResult = response.data;
        const data = packedResult && packedResult._data ? unpack(packedResult) : packedResult;

        // Only set locale from API response if not already set from URL
        if (!language && data.languageId) {
          this.$i18n.locale = data.languageId.toLowerCase();
          vm.languageId = data.languageId.toLowerCase();
        }

        vm.success = data.success;
        vm.isDemo = data.isDemo;
        vm.alreadyRedeemed = data.alreadyRedeemed;
        vm.codeInvalid = data.codeInvalid;
        vm.expired = data.expired;

        if (data.success) {

          const configuration = data.configuration;

          //todo: no need to start timer if location is needed!
          //ater that,

          vm.type = configuration.type;
          vm.showTimer = configuration.timerDuration > 0;
          vm.timerDuration = configuration.timerDuration;
          vm.timerBackgroundColor = configuration.timerBackgroundColor;
          vm.timerTopBackgroundColor = configuration.timerTopBackgroundColor;
          vm.timerTextColor = configuration.timerTextColor;
          vm.timerTopTextColor = configuration.timerTopTextColor;
          vm.timerPosition = configuration.timerPosition || 'Bottom';
          vm.timerStyle = configuration.timerType || 'Default';
          vm.timerTenthsMode = (configuration.timerTenthsMode || 'Seconds').toLowerCase();
          vm.timerUrgentColor = configuration.timerUrgentColor || '#ff4444';

          vm.backgroundTileUrl = configuration.backgroundTileUrl;
          vm.backgroundSize = configuration.backgroundSize;
          vm.backgroundRepeat = configuration.backgroundRepeat;
          if (configuration.backgroundColor)
            vm.backgroundColor = configuration.backgroundColor;

          vm.locationCheckNeeded = configuration.geolocationActive;
          vm.locationIntroText = configuration.geolocationIntroText;
          vm.locationButtonText = configuration.geolocationButtonText;
          vm.invalidLocationText = configuration.geolocationInvalidLocationText;
          vm.multipleLocationsFoundText = configuration.geolocationMultipleLocationsText;

          vm.isDemoWithValidLocation = this.$route.query.validLocation === null;
          vm.isDemoWithInvalidLocation = this.$route.query.invalidLocation === null;
          vm.isDemoWithMultipleLocations = this.$route.query.multipleLocations === null;

          if (configuration.fontFamily)
            vm.fontFamily = configuration.fontFamily;

          if (configuration.fontSize)
            vm.fontSize = configuration.fontSize;


          if (configuration.scratchAndWin) {
            vm.scratchAndWin = {
              mainVisualUrl: configuration.scratchAndWin.mainVisualUrl,
              scratchVisualUrl: configuration.scratchAndWin.scratchVisualUrl,
              revealPercentage: configuration.scratchAndWin.revealPercentage,
              keepScratchingText: configuration.scratchAndWin.keepScratchingText,
              keepScratchingTextColor: configuration.scratchAndWin.keepScratchingTextColor
            }
          } else if (configuration.wheelOfFortune) {

            let spinButtonFontFamily = configuration.wheelOfFortune.spinButtonFontFamily;
            if (!spinButtonFontFamily) spinButtonFontFamily = this.fontFamily;
            let segmentsFontFamily = configuration.wheelOfFortune.segmentsFontFamily;
            if (!segmentsFontFamily) segmentsFontFamily = this.fontFamily;
            vm.wheelOfFortune = {
              spinButtonText: configuration.wheelOfFortune.spinButtonText,
              spinButtonTextColor: configuration.wheelOfFortune.spinButtonTextColor,
              spinButtonBackgroundColor: configuration.wheelOfFortune.spinButtonBackgroundColor,
              spinItems: [],
              id: configuration.entryId,
              centerImageUrl: configuration.wheelOfFortune.centerImageUrl,
              centerBackgroundColor: configuration.wheelOfFortune.centerBackgroundColor,
              centerCircleSize: configuration.wheelOfFortune.centerCircleSize,
              centerCircleStrokeColor: configuration.wheelOfFortune.centerCircleStrokeColor,
              centerCircleStrokeWidth: configuration.wheelOfFortune.centerCircleStrokeWidth,
              spinButtonFontFamily: spinButtonFontFamily,
              spinButtonFontSize: configuration.wheelOfFortune.spinButtonFontSize,
              segmentsFontFamily: segmentsFontFamily,
              segmentsFontSize: configuration.wheelOfFortune.segmentsFontSize,
              segmentsStrokeWidth: configuration.wheelOfFortune.segmentsStrokeWidth,
              segmentsStrokeColor: configuration.wheelOfFortune.segmentsStrokeColor,
              wheelStrokeColor: configuration.wheelOfFortune.wheelStrokeColor,
              wheelStrokeWidth: configuration.wheelOfFortune.wheelStrokeWidth,
              wheelTextColor: configuration.wheelOfFortune.wheelTextColor,
              pegBackgroundColor: configuration.wheelOfFortune.pegBackgroundColor,
              wheelTextSize: configuration.fontSize,
              segmentsImageSize: configuration.wheelOfFortune.segmentsImageSize,
              segmentsImageOffset: configuration.wheelOfFortune.segmentsImageOffset,
            }

            configuration.wheelOfFortune.items.forEach(function (element) {
              vm.wheelOfFortune.spinItems.push({
                text: element.text,
                target: element.target,
                win: element.winScenario,
                imageUrl: element.imageUrl,
                color: element.backgroundColor,
                textColor: element.foregroundColor,
              })
            });

          } else if (configuration.tetris) {
            vm.tetris = {}
          } else if (configuration.memoryGame) {
            vm.memoryGame = {
              revealTime: configuration.memoryGame.revealTime,
              cardBackImageUrl: configuration.memoryGame.cardBackImageUrl,
              headingImageUrl: configuration.memoryGame.headingImageUrl,
              footerImageUrl: configuration.memoryGame.footerImageUrl,
              cardFaceImages: configuration.memoryGame.cardFaceImages,
              cardMatchedBorderColor: configuration.memoryGame.cardMatchedBorderColor,
              cardFocusBorderColor: configuration.memoryGame.cardFocusBorderColor,
              cardBorderColor: configuration.memoryGame.cardBorderColor,
              cardBorderRadius: configuration.memoryGame.cardBorderRadius,
              cardBorderSize: configuration.memoryGame.cardBorderSize,
              gridColumnsMobile: configuration.memoryGame.gridColumnsMobile,
              gridColumnsDesktop: configuration.memoryGame.gridColumnsDesktop,
              introText: configuration.memoryGame.introText,
              introTextColor: configuration.memoryGame.introTextColor,
              introTextFontSize: configuration.memoryGame.introTextFontSize,
              endGameText: configuration.memoryGame.endGameText,
              endGameTextColor: configuration.memoryGame.endGameTextColor,
              endGameTextFontSize: configuration.memoryGame.endGameTextFontSize,
              id: configuration.entryId
            }
          } else if (configuration.slotMachine) {
            vm.slotMachine = {
              spinButtonText: configuration.slotMachine.spinButtonText,
              spinButtonTextColor: configuration.slotMachine.spinButtonTextColor,
              spinButtonBackgroundColor: configuration.slotMachine.spinButtonBackgroundColor,
              spinButtonFontFamily: configuration.slotMachine.spinButtonFontFamily,
              spinButtonFontSize: configuration.slotMachine.spinButtonFontSize,
              id: configuration.entryId,
              items: configuration.slotMachine.items,
              scenario: configuration.slotMachine.scenario,
              startScenario: configuration.slotMachine.startScenario,
              slotBackgroundColor: configuration.slotMachine.backgroundColor,
              borderColor: configuration.slotMachine.borderColor,
              borderSize: configuration.slotMachine.borderSize
            }
          }

          if (vm.showTimer && !vm.locationCheckNeeded) {
            this.$nextTick(() => {
              this.getActiveCountdown().start(vm.timerDuration, vm.timerTextColor, vm.timerBackgroundColor, vm.timerTopBackgroundColor, vm.fontFamily);
            });
          }

          configuration.actions.forEach(action => {
            vm.actions.push({
              identifier: action.identifier,
              text: action.text,
              type: action.type,
              actionValue: action.actionValue,
              textColor: action.textColor,
              textBackgroundColor: action.textBackgroundColor,
              buttonText: action.buttonText,
              afterButtonPressedText: action.afterButtonPressedText,
              afterButtonPressedTextColor: action.textColor,
              afterButtonPressedTextBackgroundColor: action.textBackgroundColor,
              buttonTextColor: action.buttonTextColor,
              buttonBackgroundColor: action.buttonBackgroundColor,
              noButtonClickRequiredForAutoSend: action.noButtonClickRequiredForAutoSend
            });
          });
        }


        if (!data.success && !data.configurationError) {
          if (vm.expired)
            this.$router.replace('/notfound?expired=true');
          else if (vm.codeInvalid)
            this.$router.replace('/notfound?invalid=true');
          else if (vm.alreadyRedeemed)
            this.$router.replace('/notfound?redeemed=true');
          else
            this.$router.replace('/notfound');
        } else if (!data.success && data.configurationError) {
          this.$router.replace('/notfound?configError=true');
        }

        vm.loading = false;

      })
      .catch(e => {
        if (e.response && e.response.status == 503) {
          this.$router.replace('/notfound?maintenance=true');
          vm.loading = false;
          return;
        }
        this.$bugsnag.notify(e, {
          // Add custom metadata to the error report
          metaData: {
            custom: {
              code: vm.id,
              componentName: 'Home',
              currentRoute: this.$route.path,
            }
          }
        });
        this.$router.replace('/notfound?configError=true');
        vm.loading = false;
      });


  },

  computed: {
    countdownComponent() {
      return this.timerStyle === 'Modern' ? 'ModernCountdown' : 'VisualCountdown'
    },
    desktopViewClass() {
      let classes = [this.isDesktop ? 'desktop-view' : 'mobile-view'];

      if (this.isDesktop) {
        // Add demo class for margin adjustment
        if (this.isDemo) {
          classes.push('has-demo-header');
        }

        // Add game-specific classes
        if (this.type) {
          const gameClasses = {
            'MemoryGame': 'game-memory',
            'SlotMachine': 'game-slot',
            'WheelOfFortune': 'game-wheel',
            'ScratchAndWin': 'game-scratch',
            'Tetris': 'game-tetris'
          };

          const gameClass = gameClasses[this.type];
          if (gameClass) {
            classes.push(gameClass);
          }
        }
      }

      return classes.join(' ');
    }
  },

  methods: {
    getActiveCountdown() {
      if (this.timerPosition === 'Top') {
        return this.$refs.visualCountdownTop;
      }
      return this.$refs.visualCountdown;
    },
    // Improved iframe detection method that works better on mobile
    detectIframe() {
      try {
        // Multiple detection methods for better mobile compatibility
        return window.self !== window.top ||
          (window.frameElement && window.frameElement.nodeName === "IFRAME") ||
          window.location !== window.parent.location ||
          document.referrer !== '';
      } catch (e) {
        // Cross-origin restrictions mean we're likely in an iframe
        return true;
      }
    },

    afterButtonPressedTextStyle() {
      let style = {
        color: this.afterButtonPressedTextColor,
      }

      if (this.fontFamily)
        style.fontFamily = this.fontFamily;


      if (this.afterButtonPressedTextBackgroundColor) {
        style.backgroundColor = this.afterButtonPressedTextBackgroundColor;
        style.padding = '5px';
      }

      if (this.fontSize)
        style.fontSize = this.fontSize + 'pt';

      return style;
    },
    actionTextStyle(action) {
      let textColor = action.textColor;

      let style = {
        color: textColor,
        marginBottom: '10px'
      };

      if (this.fontFamily)
        style.fontFamily = this.fontFamily;

      if (action.textBackgroundColor) {
        style.backgroundColor = action.textBackgroundColor;
        style.padding = '5px';
      }

      if (this.fontSize)
        style.fontSize = this.fontSize + 'pt';

      return style;
    },
    buttonStyle(action) {

      let buttonTextColor = action.buttonTextColor;
      if (!buttonTextColor) buttonTextColor = "#fff";
      let buttonBackgroundColor = action.buttonBackgroundColor;
      if (!buttonBackgroundColor) buttonBackgroundColor = "#e04f1a";

      let hover = this.actionHoverState && this.actionHoverIdentifier === action.identifier;

      if (hover) {
        buttonBackgroundColor = LightenDarkenColor(buttonBackgroundColor, -20);
      }

      let style = {
        color: buttonTextColor,
        backgroundColor: buttonBackgroundColor,
        borderColor: hover ? buttonBackgroundColor : '',
      };

      if (this.fontFamily)
        style.fontFamily = this.fontFamily;

      if (this.fontSize)
        style.fontSize = this.fontSize + 'pt';

      return style;
    },
    setActionHoverState(action, hover) {
      this.actionHoverState = hover;
      this.actionHoverIdentifier = action.identifier;
    },

    timerExpired() {
      if (this.showTimer) {
        this.timerEnds = true;
        this.showTimer = false;
        if (!this.isDemo) {

          const data = { code: this.id, timerExpired: true };
          const packedData = pack(data);


          axios.post(process.env.VUE_APP_API_URL + 'play-data/u', { _data: packedData._data })
            .then(response => {
            }).catch(e => {

              if (e.response && e.response.status == 503) {
                this.$router.replace('/notfound?maintenance=true');

                return;
              }

            });
        }
        this.$router.replace('/notfound');
      }
    },
    timerStopped(diff) {
      this.timerLeftOver = diff;
      this.showTimer = false;
      if (!this.isDemo) {

        const data = { code: this.id, ended: true, timeLeft: diff };
        const packedData = pack(data);

        axios.post(process.env.VUE_APP_API_URL + 'play-data/u', { _data: packedData._data })
          .then(response => {
          })
          .catch(e => {
            if (e.response && e.response.status == 503) {
              this.$router.replace('/notfound?maintenance=true');
            }

          });
      }
    },
    onGameStart() {
      if (this.wheelOfFortune) {
        this.showTimer = false;
      }
      if (!this.isDemo) {
        // send started
        const data = { code: this.id, started: true };
        const packedData = pack(data);

        axios.post(process.env.VUE_APP_API_URL + 'play-data/u', { _data: packedData._data })
          .then(response => {
          })
          .catch(e => {
            if (e.response && e.response.status == 503) {
              this.$router.replace('/notfound?maintenance=true');
            }

          });
        ;
      }
    },
    onGameDone(gameData) {


      if (!this.showTimer) {

        if (!this.isDemo) {
          // send endOfGame with timing data if available
          const data = {
            code: this.id,
            ended: true,
            ...(gameData && {
              score: gameData.score,
              level: gameData.level,
              timePlayed: gameData.timePlayed
            })
          };
          const packedData = pack(data);
          axios.post(process.env.VUE_APP_API_URL + 'play-data/u', { _data: packedData._data })
            .then(response => {
              const packedResult = response.data;
              const data = packedResult && packedResult._data ? unpack(packedResult) : packedResult;

              // no support to show a button for now.
              if (data.endGameButtonUrl) {
                try {
                  window.parent.location.href = data.endGameButtonUrl;
                } catch (e) {
                  window.location.href = data.endGameButtonUrl;
                }
              }
            })
            .catch(e => {
              if (e.response && e.response.status == 503) {
                this.$router.replace('/notfound?maintenance=true');
              }
              return;

            });
          ;
        }
      }
      this.endOfGame = true;
      this.showTimer = false;


      if (this.actions && this.actions !== undefined && this.actions.length > 0) {
        setTimeout(() => {
          try {
            this.actions.forEach(q => {
              if (q.noButtonClickRequiredForAutoSend) {
                this.executeAutoSend(q.identifier);
              }
            });

            let actionIdentifier = this.actions.at(this.actions.length - 1);
            const ref = 'action' + actionIdentifier.identifier;
            const inputEl = this.$refs[ref];

            if (inputEl) {
              inputEl[0].scrollIntoView({ behavior: "smooth" })
            }
          } catch (e) {
            console.log(e);
          }
        }, 500);
      }
    },
    onScratchPercentage(value) {
      this.demoInfo = ' (%=' + value + ')';

    },
    onButtonClicked(identifier) {
      this.actions.forEach(value => {
        if (value.identifier === identifier) {
          if (value.afterButtonPressedText) {
            this.afterButtonPressedText = value.afterButtonPressedText;
            this.afterButtonPressedTextColor = value.afterButtonPressedTextColor;
            this.afterButtonPressedTextBackgroundColor = value.afterButtonPressedTextBackgroundColor;
          }
          if (!this.isDemo) {
            this.executeAutoSend(identifier);
          }


          if (value.type === 'Url') {
            const ref = 'action' + value.identifier;
            const inputEl = this.$refs[ref];
            (inputEl[0]).click();
          }

        }
      });

    },

    async executeAutoSend(identifier) {
      if (!this.isDemo) {
        const data = { code: this.id, button: identifier };
        const packedData = pack(data);
        axios.post(process.env.VUE_APP_API_URL + 'play-data/u', { _data: packedData._data })
          .then(response => {
            const packedResult = response.data;
            const data = packedResult && packedResult._data ? unpack(packedResult) : packedResult;
            if (data.endGameButtonUrl) {
              try {
                window.parent.location.href = data.endGameButtonUrl;
              } catch (e) {
                window.location.href = data.endGameButtonUrl;
              }
            }
          })
          .catch(e => {
            if (e.response && e.response.status == 503) {
              this.$router.replace('/notfound?maintenance=true');
            }
            return;

          });
        ;
      }
    },

    async onLocationReceived(evt) {
      let vm = this;

      if (evt.error) {
        vm.$refs.locationControl.setError(this.$t('LocationError'));
      } else {


        if (this.isDemoWithValidLocation) {
          this.locationCheckNeeded = false;

          if (vm.showTimer) {
            this.getActiveCountdown().start(vm.timerDuration, vm.timerTextColor, vm.timerBackgroundColor, vm.timerTopBackgroundColor, vm.fontFamily);
          }
          return;
        }

        if (this.isDemoWithInvalidLocation) {
          vm.$refs.locationControl.setError(this.invalidLocationText);
          return;
        }

        if (this.isDemoWithMultipleLocations) {
          let locations = [
            { value: '1', text: 'Demo Store 1' },
            { value: '2', text: 'Demo Store 2' },
          ];
          this.foundLocations = locations;
          return
        }

        // if no additional option selected
        if (this.isDemo) {
          this.locationCheckNeeded = false;

          if (vm.showTimer) {
            this.getActiveCountdown().start(vm.timerDuration, vm.timerTextColor, vm.timerBackgroundColor, vm.timerTopBackgroundColor, vm.fontFamily);
          }

          return;
        }

        // DO CALL TO SERVER AND VALIDATE RESULT

        const data = {
          code: this.id,
          latitude: evt.coordinates.lat,
          longitude: evt.coordinates.lng,
          accuracy: evt.coordinates.accuracy
        };
        const packedData = pack(data);

        axios.post(process.env.VUE_APP_API_URL + 'play/g', { _data: packedData._data })
          .then(response => {

            const packedResult = response.data;
            const result = packedResult && packedResult._data ? unpack(packedResult) : packedResult;

            if (!result.isValid) {
              vm.$refs.locationControl.setError(vm.invalidLocationText);
              return;
            }

            if (result.locations.length > 1) {
              let locations = [];
              result.locations.forEach(item => {
                locations.push({
                  value: item.id,
                  text: item.name
                });
              });
              vm.foundLocations = locations;
              return
            }

            vm.geolocationVenueId = result.locations[0].id;
            vm.locationCheckNeeded = false;

            if (vm.showTimer) {
              this.getActiveCountdown().start(vm.timerDuration, vm.timerTextColor, vm.timerBackgroundColor, vm.timerTopBackgroundColor, vm.fontFamily);
            }

          })
          .catch(e => {
            if (e.response && e.response.status == 503) {
              this.$router.replace('/notfound?maintenance=true');
            }

          });
        ;
      }


    },
    async onLocationSelected(evt) {
      this.geolocationVenueId = evt.target.value;

      const data = { code: this.id, geolocationVenueId: this.geolocationVenueId };
      const packedData = pack(data);

      axios.post(`${process.env.VUE_APP_API_URL}play-data/u`, { _data: packedData._data })
        .then(response => {
          this.locationCheckNeeded = false;

          if (this.showTimer) {
            this.getActiveCountdown().start(this.timerDuration, this.timerTextColor, this.timerBackgroundColor, this.timerTopBackgroundColor, this.fontFamily);
          }
        })
        .catch(e => {
          if (e.response && e.response.status === 503) {
            this.$router.replace('/notfound?maintenance=true');
          }
          return;
        });
    },

    // Accessibility helper methods
    getButtonAriaLabel(action) {
      // Create descriptive aria-label for buttons
      let label = action.buttonText || 'Action button';
      if (action.text) {
        // Strip HTML tags from action text for aria-label
        const textContent = action.text.replace(/<[^>]*>/g, '');
        label += ` - ${textContent}`;
      }
      return label;
    },

    announceGameState(message) {
      // Use the global screen reader announcement function
      if (this.$root.announceToScreenReader) {
        this.$root.announceToScreenReader(message);
      }
    },

    updatePageTitle(gameType) {
      // Update page title based on current game
      const titles = {
        'ScratchAndWin': 'Scratch and Win Game',
        'WheelOfFortune': 'Wheel of Fortune Game',
        'Tetris': 'Tetris Game',
        'SlotMachine': 'Slot Machine Game',
        'MemoryGame': 'Memory Game',
      };

      const gameTitle = titles[gameType] || 'Game';
      document.title = `${gameTitle} - Gamification Platform`;
    },

    // Provide viewport dimensions for child components
    getViewportDimensions() {
      if (this.isDesktop) {
        const widthMap = {
          'MemoryGame': 768,
          'SlotMachine': 600,
          'WheelOfFortune': 414,
          'ScratchAndWin': 480,  // Medium width for scratch game
          'Tetris': 414
        };

        const width = widthMap[this.type] || 414;
        return {
          width: width,
          height: 896,
          cssWidth: `${width}px`,
          cssHeight: '896px',
          isDesktop: true
        };
      }

      return {
        width: window.innerWidth,
        height: window.innerHeight,
        cssWidth: '100vw',
        cssHeight: '100vh',
        isDesktop: false
      };
    },

    // Update CSS custom properties on window resize
    updateViewportProperties() {
      if (this.$el && this.$el.querySelector) {
        const dimensions = this.getViewportDimensions();
        const bgElement = this.$el.querySelector('.bg');

        if (bgElement) {
          bgElement.style.setProperty('--viewport-width', dimensions.cssWidth);
          bgElement.style.setProperty('--viewport-height', dimensions.cssHeight);
          bgElement.style.setProperty('--viewport-width-px', dimensions.width);
          bgElement.style.setProperty('--viewport-height-px', dimensions.height);
        }
      }
    },

  },

  // Lifecycle hooks for accessibility
  mounted() {
    const md = new MobileDetect(window.navigator.userAgent);
    if (!this.$route.query.embed && !md.mobile()) {
      this.isDesktop = true;
    }

    // Update page title when component mounts
    if (this.type) {
      this.updatePageTitle(this.type);
    }

    // Set focus to main content for screen readers
    this.$nextTick(() => {
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.focus();
      }

      // Update viewport properties after DOM is ready
      this.updateViewportProperties();
    });

    // Listen for window resize on desktop
    if (this.isDesktop) {
      window.addEventListener('resize', this.updateViewportProperties);
    }
  },

  beforeDestroy() {
    if (this.isDesktop) {
      window.removeEventListener('resize', this.updateViewportProperties);
    }
  },

  watch: {
    // Watch for game type changes and update title
    type(newType) {
      if (newType) {
        this.updatePageTitle(newType);
        this.announceGameState(`${newType} game loaded`);
        // Update viewport properties when game type changes
        this.$nextTick(() => {
          this.updateViewportProperties();
        });
      }
    },

    // Watch for game end state
    endOfGame(isEnded) {
      if (isEnded) {
        this.announceGameState('Game completed. Results are now available.');
      }
    },

    // Watch for loading state changes
    loading(isLoading) {
      if (!isLoading && this.type) {
        this.announceGameState(`${this.type} game is ready to play`);
      }
    }
  },
}
</script>

<style>
/* Screen reader only content */
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

iframe {
  display: block;
  height: 100%;
  min-height: 100%;
  width: 100%;
  margin: 0;
}

.center-screen {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  min-height: 100vh;
  background-color: black;
}

.desktop-view {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  position: relative;
  background-size: auto;
  background-repeat: repeat;
}

/* Desktop demo header - full width at top */
.desktop-demo-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 10;
}

.desktop-demo-header .alert {
  margin: 0;
  border-radius: 0;
}

/* Target the .bg element which has the location-blur component */
.desktop-view .bg {
  max-width: 414px;
  /* iPhone 6/7/8 Plus width */
  width: 414px;
  margin: 20px auto;
  /* Default margin */
  overflow: visible;
  /* Allow content to be visible */
  height: auto;
  min-height: 736px;
  /* iPhone 6/7/8 Plus height */
  position: relative;
  display: flex;
  flex-direction: column;
  background: transparent !important;
  /* Transparent to show parent background */
}

/* Adjust margin when demo header is present */
.desktop-view.has-demo-header .bg {
  margin-top: 80px;
}

/* Support for different game widths on desktop */
.desktop-view.game-memory .bg {
  max-width: 768px;
  /* Wider for memory game */
  width: 768px;
}

.desktop-view.game-slot .bg {
  max-width: 600px;
  /* Medium width for slot machine */
  width: 600px;
}

.desktop-view.game-scratch .bg {
  max-width: 480px;
  /* Medium width for scratch and win */
  width: 480px;
}

/* Make child components aware of viewport constraints */
.desktop-view .bg {
  --viewport-width: 414px;
  --viewport-height: 896px;
  --viewport-width-px: 414;
  --viewport-height-px: 896;
}

.desktop-view.game-memory .bg {
  --viewport-width: 768px;
  --viewport-width-px: 768;
}

.desktop-view.game-slot .bg {
  --viewport-width: 600px;
  --viewport-width-px: 600;
}

.desktop-view.game-scratch .bg {
  --viewport-width: 480px;
  --viewport-width-px: 480;
}

/* Ensure proper scrolling on desktop */
.desktop-view [role="main"] {
  overflow: visible;
  -webkit-overflow-scrolling: touch;
  height: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Override canvas and calculated widths for desktop view */
.desktop-view canvas {
  max-width: 100% !important;
  height: auto !important;
}

/* Constrain all game content within viewport */
.desktop-view section {
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

/* Make games responsive within the constrained viewport */
.desktop-view .memory-game-container,
.desktop-view .scratch-game-container,
.desktop-view .wheel-container,
.desktop-view .slot-container {
  width: 100% !important;
  max-width: 100% !important;
}

/* Specific constraints for ScratchAndWin content */
.desktop-view.game-scratch section[role="region"] {
  max-width: 480px !important;
  overflow: hidden !important;
  position: relative;
}

/* Scratch game container - no transform to preserve interaction */
.desktop-view.game-scratch scratch-and-win,
.desktop-view.game-scratch scratch-card {
  display: block !important;
  max-width: 480px !important;
  margin: 0 auto !important;
}

/* Target any fixed-width containers */
.desktop-view.game-scratch [style*="width:"],
.desktop-view.game-scratch .scratch-container {
  max-width: 480px !important;
}

/* Constrain all images inside scratch game */
.desktop-view.game-scratch img {
  max-width: 100% !important;
  height: auto !important;
  object-fit: contain !important;
}

/* Constrain scratch card container and canvases */
.desktop-view.game-scratch div[style*="width:"] {
  max-width: 480px !important;
  margin: 0 auto !important;
}

.desktop-view.game-scratch canvas {
  max-width: 480px !important;
  height: auto !important;
}

/* Override inline styles for proper centering */
.desktop-view.game-scratch .scratch-card-container {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

/* Specific fix for WheelOfFortune container */
.desktop-view div[style*="height: 465px"] {
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
  min-height: 465px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 20px 0;
}

/* Fix canvas sizing - ensure full wheel is visible */
.desktop-view canvas {
  max-width: calc(100% - 40px) !important;
  max-height: calc(100% - 40px) !important;
  width: auto !important;
  height: auto !important;
  margin: 0 auto !important;
  display: block !important;
  object-fit: contain;
}

/* Fix Push to spin button - scale it down proportionally */
.desktop-view button[style*="position: absolute"] {
  /* Scale down the button size */
  transform: translate(-50%, -50%) scale(0.8) !important;
  max-width: 160px !important;
  font-size: 0.9em !important;
}

/* Ensure wheel container maintains aspect ratio */
.desktop-view .wheel-container,
.desktop-view div[style*="position: relative"] {
  position: relative !important;
  width: 100% !important;
  max-width: 100% !important;
}

/* Ensure game sections have proper layout */
.desktop-view section[role="region"] {
  width: 100%;
  overflow: visible;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
}



.mobile-view {
  height: 100%;
  min-height: 100%;
  width: 100%;
  margin: 0;
  background-repeat: repeat;
}

.mobile-view .bg {
  --viewport-width: 100vw;
  --viewport-height: 100vh;
}

.bg {
  height: 100%;
  background-repeat: repeat;
}

/* Keep mobile demo alerts visible on mobile only */
.desktop-view .alert:not(.desktop-demo-header .alert),
.desktop-view .alert-inframe {
  display: none !important;
}

.spinner {
  position: absolute;
  top: 50%;
  left: 50%;
  margin-top: -100px;
  margin-left: -100px;

  width: 200px;
  height: 200px;
  /*background-color: #44912d;*/

  border-radius: 100%;
  -webkit-animation: sk-scaleout 1.0s infinite ease-in-out;
  animation: sk-scaleout 1.0s infinite ease-in-out;
}

@-webkit-keyframes sk-scaleout {
  0% {
    -webkit-transform: scale(0)
  }

  100% {
    -webkit-transform: scale(1.0);
    opacity: 0;
  }
}

@keyframes sk-scaleout {
  0% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }

  100% {
    -webkit-transform: scale(1.0);
    transform: scale(1.0);
    opacity: 0;
  }
}

.alert-inframe {
  position: relative;
  font-size: x-small;
}

/*! CSS Used from: Embedded */
*,
*::before,
*::after {
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

select {
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
}

select {
  text-transform: none;
}

select {
  word-wrap: normal;
}

.form-control {
  display: block;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 6px 12px;
  padding: 0.375rem 0.75rem;
  font-size: 16px;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  -webkit-transition: border-color 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
  transition: border-color 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
}

.form-control::-ms-expand {
  background-color: transparent;
  border: 0;
}

.form-control:focus {
  color: #495057;
  background-color: #fff;
  border-color: #80bdff;
  outline: 0;
  -webkit-box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, .25);
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, .25);
}

.form-control::-webkit-input-placeholder {
  color: #6c757d;
  opacity: 1;
}

.form-control:-ms-input-placeholder {
  color: #6c757d;
  opacity: 1;
}

.form-control::-ms-input-placeholder {
  color: #6c757d;
  opacity: 1;
}

.form-control::placeholder {
  color: #6c757d;
  opacity: 1;
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

.custom-select {
  display: inline-block;
  width: 100%;
  height: calc(1.5em + 0.75rem + 2px);
  padding: 6px 28px 6px 12px;
  padding: 0.375rem 1.75rem 0.375rem 0.75rem;
  font-size: 16px;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #495057;
  vertical-align: middle;
  background: url(data:image/svg+xml,%3csvg xmlns= 'http://www.w3.org/2000/svg' viewBox= '0 0 4 5' %3e%3cpath fill= '%23343a40' d= 'M2 0L0 2h4zm0 5L0 3h4z' /%3e%3c/svg%3e) no-repeat right 12 px center / 8 px 10 px;
  background: url(data:image/svg+xml,%3csvg xmlns= 'http://www.w3.org/2000/svg' viewBox= '0 0 4 5' %3e%3cpath fill= '%23343a40' d= 'M2 0L0 2h4zm0 5L0 3h4z' /%3e%3c/svg%3e) no-repeat right 0.75 rem center / 8 px 10 px;
  background-color: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

.custom-select:focus {
  border-color: #80bdff;
  outline: 0;
  -webkit-box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, .25);
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, .25);
}

.custom-select:disabled {
  color: #6c757d;
  background-color: #e9ecef;
}

.custom-select::-ms-expand {
  display: none;
}

.custom-select {
  -webkit-transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
  transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
  transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;
}
</style>
